import { useDispatch, useSelector } from "react-redux";

import { MAP_STYLES } from "@/constants/map";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";
import { setMapStyle } from "@/store/slice/mapSlice";
import { transformStyle } from "@/utils/map";

import BaseMapControl, {
  type BaseMapStyle,
  type BaseMapStyleKey
} from "../controls/BaseMapControl";

type Props<TKey extends BaseMapStyleKey> = {
  /** Defaults to all basemaps; a map with no Mapbox token narrows this to the
   * token-free ones. */
  styles?: Record<TKey, BaseMapStyle>;
};

/**
 * Redux/`MapProvider` adapter around the generic `BaseMapControl` — it owns
 * where the selection lives and how the shared map applies it.
 */
export default function BaseMapMenu<TKey extends BaseMapStyleKey>({
  styles
}: Props<TKey>) {
  const dispatch = useDispatch();
  const { baseMapRef, map } = useMap();
  const { baseMap } = useSelector((state: RootState) => state.map);

  const availableStyles = (styles ?? MAP_STYLES) as Record<TKey, BaseMapStyle>;

  const handleClickBaseMap = (key: TKey) => {
    dispatch(setMapStyle(key));

    const nextStyle = availableStyles[key];

    if (nextStyle == null) return;

    if (JSON.stringify(baseMapRef.current) === JSON.stringify(nextStyle))
      return;

    map?.setStyle(nextStyle.value, {
      diff: true,
      transformStyle
    });

    baseMapRef.current = nextStyle.value;
  };

  return (
    <BaseMapControl
      styles={availableStyles}
      selected={baseMap}
      onSelect={handleClickBaseMap}
    />
  );
}
